import { KeplerAggregationType, KeplerFieldType, KeplerSavedLayer, KeplerSavedLayerConfig } from './kepler-types'
import { Modify } from 'features/common/utils/typeUtils'
import { LegendHexColorMaps } from './legend-types'

export type RgbColor = [number, number, number]

export type ColorRangeMap = [string | number | null, string][]

export type ColorRange = {
  name: string
  category: string
  type: string // sequential, qualitative, etc

  colors: string[]
  colorMap?: [string | number | null, string][]

  /**
   * If color range scale is custom, then it may be actually "logarithmic" one which is
   * implemented as "custom", but it's not custom at all
   */
  uiCustomScaleType?: string

  /**
   * If this color range is _driven_ by Hex Color Column
   */
  hexColor?: boolean
}

export type CustomMarkerEntry = {
  value: string | null
  markerUrl?: string
  markerId?: string
}

export type CustomMarkersRange = {
  othersMarkerId?: string
  othersMarker?: string
  markerMap: CustomMarkerEntry[]
}

export enum RasterStyleType {
  Rgb = 'Rgb',
  ColorRange = 'ColorRange',
  UniqueValues = 'UniqueValues'
}

export type ColorBand = 'red' | 'green' | 'blue' | 'alpha'

export type RasterLayerConfigColorBand = {
  band: ColorBand
  type: 'none' | 'band' | 'expression'
  value: string // band name or expression
}

export type CartoMapLayerVisConfig = {
  opacity?: number

  // fill color for points, custom icons, polygons, cells
  filled?: boolean
  colorRange?: ColorRange | null
  colorAggregation?: KeplerAggregationType | null

  // stroke color - line color or outline color for points, polygons, cells
  stroked?: boolean
  strokeColor?: RgbColor | null
  initialStrokeColor?: RgbColor | null
  strokeColorRange?: ColorRange | null
  strokeColorAggregation?: KeplerAggregationType | null
  strokeOpacity?: number

  // size - width of lines, size of outline for points, polygons, cells (pixels)
  thickness?: number // value if no range is used
  sizeRange?: [number, number] | null | undefined
  sizeAggregation?: KeplerAggregationType | null

  // radius - radius of points, size of custom marker icons (pixels)
  radius?: number // value if no range is used
  radiusRange?: [number, number] | null | undefined
  radiusAggregation?: KeplerAggregationType | null

  // height
  enable3d?: boolean
  heightRange?: [number, number] | null | undefined
  heightAggregation?: KeplerAggregationType | null

  weightRange?: [number, number] | null | undefined
  weightAggregation?: string
  elevationScale: number

  rasterStyleType?: RasterStyleType
  colorBands?: RasterLayerConfigColorBand[]
  uniqueValuesColorRange?: ColorRange

  // custom markers for point layers
  customMarkers?: boolean
  customMarkersId?: string
  customMarkersUrl?: string
  customMarkersRange?: CustomMarkersRange | null

  // cluster level for cluster tile layers
  clusterLevel?: number
  isTextVisible?: boolean

  // deprecated, keeping for old code and documentation
  outline?: boolean
  worldUnitSize: number
}

export type Field = {
  name: string
  type: KeplerFieldType
}

export type LayerTextLabel = {
  field: Field | null
  color: RgbColor
  outlineColor: RgbColor
  anchor: 'start' | 'end' | 'middle'
  alignment: 'center' | 'bottom' | 'top'
  size: number
}

export type CartoMapLayerConfig = Modify<
  KeplerSavedLayerConfig,
  {
    visConfig: CartoMapLayerVisConfig
    textLabel: LayerTextLabel[]
  }
> & {
  visibilityByZoom?: {
    min: number
    max: number
  }
}

export type VisualChannelField = Field & {
  channelScaleType?: string // not sure if this is actually used anywhere
  colorColumn?: string
}

export const LayerTypes = {
  // placeholder layer that renders nothing, but allows to specify spatial data settings
  Unknown: 'unknown' as const,

  // old document mode types
  Point: 'point' as const,
  GeoJSON: 'geojson' as const,
  Line: 'line' as const,
  S2: 's2' as const,
  HexagonId: 'hexagonId' as const, // h3 column in data
  Grid: 'grid' as const, // points visualized locally as quad grid
  Hexagon: 'hexagon' as const, // points visualized locally as hexagon grid,
  Heatmap: 'heatmap' as const,
  Cluster: 'cluster' as const,
  Trip: 'trip' as const,

  // tile-based layers
  Tileset: 'tileset' as const,
  Quadbin: 'quadbin' as const,
  H3: 'h3' as const,
  HeatmapTile: 'heatmapTile' as const,
  ClusterTile: 'clusterTile' as const,
  Raster: 'raster' as const
} as const

export type LayerType =
  | typeof LayerTypes.Unknown
  | typeof LayerTypes.Point
  | typeof LayerTypes.GeoJSON
  | typeof LayerTypes.Line
  | typeof LayerTypes.S2
  | typeof LayerTypes.HexagonId
  | typeof LayerTypes.Grid
  | typeof LayerTypes.Hexagon
  | typeof LayerTypes.Heatmap
  | typeof LayerTypes.Cluster
  | typeof LayerTypes.Trip
  | typeof LayerTypes.Tileset
  | typeof LayerTypes.Quadbin
  | typeof LayerTypes.H3
  | typeof LayerTypes.HeatmapTile
  | typeof LayerTypes.ClusterTile
  | typeof LayerTypes.Raster

export type CartoMapLayer = Modify<
  KeplerSavedLayer,
  {
    type: LayerType
    config: CartoMapLayerConfig
    visualChannels: Record<string, string | VisualChannelField | null>
    legendHexColorMaps?: LegendHexColorMaps

    // @deprecated - replaced by legendHexColorMaps
    legendSettings?: LegendHexColorMaps
  }
>
